/**
 * WebhookRuleLogsTab - Aba de logs para regras de webhook
 * 
 * Exibe tabela paginada de logs com filtros e expansão
 * para ver payload e detalhes de erro.
 * 
 * @version 1.0.0
 * @date 2026-01-30
 */

import { useState, useEffect, Fragment } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  ChevronDown, 
  ChevronUp, 
  Clock,
  Activity
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { useRoutingWebhookLogs } from '@/hooks/useRoutingWebhookLogs';
import type { RoutingWebhookLog } from './types';

// ============= INTERFACES =============

interface WebhookRuleLogsTabProps {
  /** ID da regra de roteamento */
  ruleId: string;
}

// ============= COMPONENTE PRINCIPAL =============

export function WebhookRuleLogsTab({ ruleId }: WebhookRuleLogsTabProps) {
  // Hook de logs
  const { logs, isLoadingLogs, logsTotal, fetchLogs } = useRoutingWebhookLogs();

  // Estados locais
  const [filter, setFilter] = useState<'all' | 'success' | 'error'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  // Carregar logs ao montar ou mudar filtros
  useEffect(() => {
    fetchLogs(ruleId, page, pageSize, filter === 'all' ? undefined : filter);
  }, [ruleId, page, pageSize, filter, fetchLogs]);

  // Resetar página ao mudar filtro
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // Toggle expansão
  const toggleExpand = (logId: string) => {
    setExpandedLogId(prev => (prev === logId ? null : logId));
  };

  // Renderizar badge de status
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20 hover:bg-green-500/20">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Sucesso
          </Badge>
        );
      case 'error':
        return (
          <Badge className="bg-red-500/10 text-red-600 border-red-500/20 hover:bg-red-500/20">
            <XCircle className="h-3 w-3 mr-1" />
            Erro
          </Badge>
        );
      case 'validation_error':
        return (
          <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 hover:bg-amber-500/20">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Validação
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Calcular total de páginas
  const totalPages = Math.ceil(logsTotal / pageSize);

  // Loading state
  if (isLoadingLogs && logs.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // Estado vazio
  if (!isLoadingLogs && logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="p-4 rounded-full bg-muted/50 mb-4">
          <Activity className="h-8 w-8 text-muted-foreground/50" />
        </div>
        <p className="text-muted-foreground font-medium">Nenhum log encontrado</p>
        <p className="text-sm text-muted-foreground/70 mt-1">
          {filter !== 'all' 
            ? 'Tente remover o filtro para ver todos os logs'
            : 'As requisições ao webhook aparecerão aqui'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header com filtro */}
      <div className="flex items-center justify-between">
        <Select value={filter} onValueChange={(v) => setFilter(v as 'all' | 'success' | 'error')}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="success">Sucesso</SelectItem>
            <SelectItem value="error">Erro</SelectItem>
          </SelectContent>
        </Select>

        <span className="text-sm text-muted-foreground">
          {logsTotal} registro{logsTotal !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Tabela de logs */}
      <div className="rounded-lg border bg-background/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="w-44">Data/Hora</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead className="w-28">IP</TableHead>
              <TableHead className="w-20 text-right">Tempo</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log) => (
              <Fragment key={log.id}>
                {/* Linha principal */}
                <TableRow 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleExpand(log.id)}
                >
                  <TableCell className="font-mono text-xs">
                    <div className="space-y-0.5">
                      <p>{format(new Date(log.created_at), 'dd/MM/yyyy HH:mm:ss')}</p>
                      <p className="text-muted-foreground">
                        {formatDistanceToNow(new Date(log.created_at), { 
                          addSuffix: true, 
                          locale: ptBR 
                        })}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>{renderStatusBadge(log.status)}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {log.contact_phone || '-'}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {log.request_ip || '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {log.processing_time_ms}ms
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      {expandedLogId === log.id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>

                {/* Linha expandida com detalhes */}
                {expandedLogId === log.id && (
                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                    <TableCell colSpan={6} className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Payload recebido */}
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-muted-foreground uppercase">
                            Payload Recebido
                          </p>
                          <pre className="text-xs bg-background rounded-lg p-3 overflow-auto max-h-48 border">
                            {JSON.stringify(log.request_payload, null, 2)}
                          </pre>
                        </div>

                        {/* Detalhes da execução */}
                        <div className="space-y-4">
                          {/* Mensagem de erro (se houver) */}
                          {log.error_message && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-red-600 uppercase">
                                Mensagem de Erro
                              </p>
                              <div className="text-sm bg-red-50 dark:bg-red-950/30 text-red-600 rounded-lg p-3 border border-red-200 dark:border-red-900">
                                {log.error_message}
                                {log.error_code && (
                                  <span className="block text-xs mt-1 opacity-70">
                                    Código: {log.error_code}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Estatísticas de ações */}
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase">
                              Ações Executadas
                            </p>
                            <div className="flex gap-4">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                                <span className="text-sm">
                                  {log.actions_executed} executada{log.actions_executed !== 1 ? 's' : ''}
                                </span>
                              </div>
                              {log.actions_failed > 0 && (
                                <div className="flex items-center gap-2">
                                  <XCircle className="h-4 w-4 text-red-500" />
                                  <span className="text-sm">
                                    {log.actions_failed} falhou/falharam
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* IDs gerados */}
                          {(log.contact_id || log.conversation_id) && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-muted-foreground uppercase">
                                Registros Criados
                              </p>
                              <div className="text-xs font-mono space-y-1">
                                {log.contact_id && (
                                  <p>Contato: {log.contact_id.substring(0, 8)}...</p>
                                )}
                                {log.conversation_id && (
                                  <p>Conversa: {log.conversation_id.substring(0, 8)}...</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <PaginationFooter
          totalItems={logsTotal}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
}
